import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, CheckCircle, Trash2, RefreshCw, Search, PlusSquare, AlertTriangle, ServerCrash, XCircle, Wifi, WifiOff, CheckCircle2, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { wpApprove, wpDelete, wpGet, wpList, wpTestConnection, formatWpError } from "@/lib/wpSubmissions";
import { validateWpClaim, formatValidationErrors } from "@/lib/wpClaimValidation";

type AnyObj = Record<string, any>;

interface ConnectionError {
  title: string;
  description: string;
  isWpError: boolean;
  isRetryable: boolean;
}

interface ConnectionStatus {
  tested: boolean;
  success: boolean;
  message: string;
}

function getWpId(item: AnyObj): string {
  return String(item?.id ?? item?.wp_id ?? item?.ID ?? "");
}

function safeStr(v: any, fallback = "Unknown") {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  return s.length ? s : fallback;
}

function getClaimSummary(item: AnyObj): { claimNumber: string; policyholder: string; adjuster: string; lossDate: string } {
  const claimNumber = safeStr(item?.claim_number ?? item?.claimNumber ?? item?.claim);
  const policyholder = safeStr(item?.policyholder_name ?? item?.policyholderName ?? item?.policyholder?.name);
  const adjuster = safeStr(item?.adjuster_name ?? item?.adjusterName ?? item?.adjuster?.name);
  const lossDate = safeStr(item?.loss_date ?? item?.lossDate);
  return { claimNumber, policyholder, adjuster, lossDate };
}

function mapWpClaimToSupabaseClaim(input: AnyObj, userId: string) {
  // Best-effort mapping based on how TPSV2 consumes WP claim submissions.
  const adjusterObj = input?.adjuster ?? {};
  const policyholderObj = input?.policyholder ?? {};
  const addr = policyholderObj?.address ?? {};

  return {
    user_id: userId,

    adjuster_name: safeStr(input?.adjuster_name ?? adjusterObj?.name),
    adjuster_email: safeStr(input?.adjuster_email ?? adjusterObj?.email),
    adjuster_phone: safeStr(input?.adjuster_phone ?? adjusterObj?.phone),
    adjuster_company: safeStr(input?.adjuster_company ?? adjusterObj?.company),
    adjuster_license: safeStr(input?.adjuster_license ?? adjusterObj?.license ?? "Unknown"),

    claim_number: safeStr(input?.claim_number),
    loss_date: safeStr(input?.loss_date),
    claim_type: safeStr(input?.claim_type ?? input?.loss_type ?? "Other"),

    policyholder_name: safeStr(input?.policyholder_name ?? policyholderObj?.name ?? `${policyholderObj?.first_name ?? ""} ${policyholderObj?.last_name ?? ""}`.trim()),
    policyholder_email: safeStr(input?.policyholder_email ?? policyholderObj?.email),
    policyholder_phone: safeStr(input?.policyholder_phone ?? policyholderObj?.phone),

    policyholder_address: safeStr(addr?.address ?? addr?.address_1 ?? input?.policyholder_address),
    policyholder_city: safeStr(addr?.city ?? input?.policyholder_city),
    policyholder_state: safeStr(addr?.state ?? input?.policyholder_state),
    policyholder_zip: safeStr(addr?.zip ?? input?.policyholder_zip),

    family_dynamics: input?.dynamics ? JSON.stringify(input.dynamics) : (input?.family_dynamics ? String(input.family_dynamics) : null),
    approved_terms: input?.terms ? JSON.stringify(input.terms) : (input?.approved_terms ? String(input.approved_terms) : null),
    
    submitted_by: safeStr(input?.submitted_by ?? input?.adjuster_name ?? adjusterObj?.name),
    submitted_at: input?.submitted_at ?? new Date().toISOString(),
  };
}

export default function AdminWpClaimSubmissions() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<AnyObj[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const perPage = 50;

  const [connectionError, setConnectionError] = useState<ConnectionError | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [testing, setTesting] = useState(false);
  const [notifying, setNotifying] = useState(false);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState<AnyObj | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  const sendNotification = async (item: AnyObj) => {
    const s = getClaimSummary(item);
    try {
      const { error } = await supabase.functions.invoke("send-wp-submission-notification", {
        body: {
          submissionType: "claim",
          submissionId: getWpId(item),
          submissionDetails: {
            claimNumber: s.claimNumber,
            submitterEmail: item?.adjuster_email || item?.adjuster?.email,
            submitterName: s.adjuster,
          },
        },
      });
      if (error) throw error;
      toast.success("Email notification sent to admins");
    } catch (e: any) {
      console.error("Failed to send notification:", e);
      toast.error("Failed to send notification: " + (e.message || "Unknown error"));
    }
  };

  const handleNotifyAll = async () => {
    if (items.length === 0) {
      toast.info("No submissions to notify about");
      return;
    }
    setNotifying(true);
    try {
      const { error } = await supabase.functions.invoke("send-wp-submission-notification", {
        body: {
          submissionType: "claim",
          submissionId: "batch",
          submissionDetails: {
            claimNumber: `${items.length} pending claim(s)`,
          },
        },
      });
      if (error) throw error;
      toast.success("Notification sent to all admins");
    } catch (e: any) {
      console.error("Failed to send notification:", e);
      toast.error("Failed to send notification: " + (e.message || "Unknown error"));
    } finally {
      setNotifying(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setConnectionStatus(null);
    setConnectionError(null);
    
    try {
      const result = await wpTestConnection();
      setConnectionStatus({
        tested: true,
        success: result.success,
        message: result.message,
      });
      
      if (result.success) {
        toast.success("WordPress connection successful");
      } else {
        toast.error(result.message);
      }
    } catch (e: any) {
      const formatted = formatWpError(e);
      setConnectionStatus({
        tested: true,
        success: false,
        message: formatted.description,
      });
      toast.error(formatted.description);
    } finally {
      setTesting(false);
    }
  };

  const fetchList = async () => {
    setLoading(true);
    setConnectionError(null);
    try {
      const res = await wpList<AnyObj>("claims", { page, perPage });
      setItems(res.items || []);
      setTotal(res.total || 0);
      // Clear any previous connection status on successful fetch
      if (connectionStatus?.success === false) {
        setConnectionStatus(null);
      }
    } catch (e: any) {
      console.error(e);
      const formatted = formatWpError(e);
      setConnectionError(formatted);
      toast.error(formatted.description);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((it) => JSON.stringify(it).toLowerCase().includes(q));
  }, [items, search]);

  const handleView = async (it: AnyObj) => {
    setViewOpen(true);
    setViewItem(it);

    const id = getWpId(it);
    if (!id) return;

    setViewLoading(true);
    try {
      const res = await wpGet<AnyObj>("claims", id);
      setViewItem(res.item);
    } catch (e: any) {
      console.error(e);
      const formatted = formatWpError(e);
      toast.error(formatted.description);
    } finally {
      setViewLoading(false);
    }
  };

  const handleApprove = async (it: AnyObj) => {
    const id = getWpId(it);
    if (!id) return toast.error("Missing WordPress id");

    try {
      await wpApprove("claims", id);
      toast.success("Approved in WordPress");
      fetchList();
    } catch (e: any) {
      console.error(e);
      const formatted = formatWpError(e);
      toast.error(formatted.description);
    }
  };

  const handleDelete = async (it: AnyObj) => {
    const id = getWpId(it);
    if (!id) return toast.error("Missing WordPress id");

    if (!confirm("Delete this submission from WordPress?")) return;

    try {
      await wpDelete("claims", id);
      toast.success("Deleted from WordPress");
      fetchList();
    } catch (e: any) {
      console.error(e);
      const formatted = formatWpError(e);
      toast.error(formatted.description);
    }
  };

  const handleImportToClaims = async (it: AnyObj) => {
    try {
      const { data: sessionResp } = await supabase.auth.getSession();
      const userId = sessionResp?.session?.user?.id;
      if (!userId) {
        toast.error("Not logged in");
        return;
      }

      const id = getWpId(it);
      // Pull full object from WP if possible to get nested fields.
      let full = it;
      if (id) {
        try {
          const res = await wpGet<AnyObj>("claims", id);
          full = res.item;
        } catch {
          // fall back to list row
        }
      }

      const payload = mapWpClaimToSupabaseClaim(full, userId);

      // Validate before inserting
      const validation = validateWpClaim(payload);
      if (!validation.success) {
        toast.error(formatValidationErrors(validation.errors!));
        return;
      }

      const { error } = await supabase.from("claims").insert(validation.data as any);
      if (error) throw error;

      toast.success("Created a Claim record in TPS Housing");
    } catch (e: any) {
      console.error(e);
      const formatted = formatWpError(e);
      toast.error(formatted.description);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <CardTitle>WordPress Claim Submissions</CardTitle>
              <CardDescription>
                Pending claim submissions coming from tpshousing.com (WordPress forms).
              </CardDescription>
            </div>
            <Button
              variant={connectionStatus?.success ? "outline" : "secondary"}
              size="sm"
              onClick={handleTestConnection}
              disabled={testing}
              className="shrink-0"
            >
              {testing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Testing…
                </>
              ) : connectionStatus?.success ? (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                  Connected
                </>
              ) : connectionStatus?.tested ? (
                <>
                  <WifiOff className="h-4 w-4 mr-2 text-destructive" />
                  Test Failed
                </>
              ) : (
                <>
                  <Wifi className="h-4 w-4 mr-2" />
                  Test Connection
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {connectionStatus && !connectionStatus.success && (
            <Alert variant="destructive" className="mb-4">
              <WifiOff className="h-4 w-4" />
              <AlertTitle className="flex items-center gap-2">
                Connection Test Failed
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 ml-auto"
                  onClick={() => setConnectionStatus(null)}
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </AlertTitle>
              <AlertDescription className="mt-2">
                <p>{connectionStatus.message}</p>
              </AlertDescription>
            </Alert>
          )}

          {connectionStatus?.success && (
            <Alert className="mb-4 border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Connection Successful</AlertTitle>
              <AlertDescription>{connectionStatus.message}</AlertDescription>
            </Alert>
          )}

          {connectionError && (
            <Alert variant="destructive" className="mb-4">
              {connectionError.isWpError ? (
                <ServerCrash className="h-4 w-4" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}
              <AlertTitle className="flex items-center gap-2">
                {connectionError.title}
                {connectionError.isRetryable && (
                  <Badge variant="outline" className="text-xs">Retried automatically</Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 ml-auto"
                  onClick={() => setConnectionError(null)}
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </AlertTitle>
              <AlertDescription className="mt-2">
                <p>{connectionError.description}</p>
                {connectionError.isWpError && (
                  <p className="text-xs mt-2 opacity-80">
                    This is a WordPress server issue. Contact your WordPress administrator or check the server logs.
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between mb-4">
            <div className="flex items-center gap-2 w-full md:max-w-md">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search submissions…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleNotifyAll} disabled={notifying || items.length === 0}>
                <Mail className={`h-4 w-4 mr-2 ${notifying ? "animate-pulse" : ""}`} />
                {notifying ? "Sending…" : "Notify Admins"}
              </Button>
              <Button variant="outline" onClick={fetchList} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary">Total: {total}</Badge>
            <Badge variant="outline">Page {page + 1} / {pageCount}</Badge>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead style={{ width: 80 }}>ID</TableHead>
                  <TableHead>Claim #</TableHead>
                  <TableHead>Policyholder</TableHead>
                  <TableHead style={{ width: 150 }}>Submitted By</TableHead>
                  <TableHead style={{ width: 150 }}>Submitted At</TableHead>
                  <TableHead style={{ width: 100 }}>Loss Date</TableHead>
                  <TableHead style={{ width: 340 }}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((it) => {
                  const s = getClaimSummary(it);
                  return (
                    <TableRow key={getWpId(it) || JSON.stringify(it)}>
                      <TableCell className="font-mono text-xs">{getWpId(it) || "—"}</TableCell>
                      <TableCell>{s.claimNumber}</TableCell>
                      <TableCell>{s.policyholder}</TableCell>
                      <TableCell>{it?.submitted_by || s.adjuster}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {it?.submitted_at ? new Date(it.submitted_at).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell>{s.lossDate}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => handleView(it)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => sendNotification(it)}>
                            <Mail className="h-4 w-4 mr-2" />
                            Notify
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => handleImportToClaims(it)}>
                            <PlusSquare className="h-4 w-4 mr-2" />
                            Create Claim
                          </Button>
                          <Button size="sm" onClick={() => handleApprove(it)}>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(it)}>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!loading && filtered.length === 0 && !connectionError && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      No pending submissions.
                    </TableCell>
                  </TableRow>
                )}

                {!loading && connectionError && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Could not load submissions. See error above.
                    </TableCell>
                  </TableRow>
                )}

                {loading && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Loading…
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <Button variant="outline" disabled={page <= 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Showing {filtered.length} of {items.length} on this page
            </div>
            <Button
              variant="outline"
              disabled={page >= pageCount - 1 || loading}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              Next
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Submission Details</DialogTitle>
            <DialogDescription>
              {viewLoading ? "Loading latest details from WordPress…" : ""}
            </DialogDescription>
          </DialogHeader>
          <pre className="text-xs bg-muted p-4 rounded-lg overflow-auto max-h-[60vh]">
            {JSON.stringify(viewItem, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
